import { useEffect, useRef } from "react";

/** Open Helpdesk modals, newest last, so Escape closes only the one on top. */
const openModals: symbol[] = [];

/**
 * Calls `onEscape` when Escape is pressed while this modal is the top one, and
 * not already handled by a control inside it (e.g. an open dropdown). Shared by
 * HelpdeskFormModal and HelpdeskConfirmModal so stacked modals close one at a time.
 */
const useTopmostEscape = (onEscape: () => void, enabled = true): void => {
  // Latest callback without re-registering (which would reorder the stack).
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const token = Symbol("helpdeskModal");
    openModals.push(token);
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape" || event.defaultPrevented) return;
      if (openModals[openModals.length - 1] !== token) return;
      event.preventDefault();
      if (enabledRef.current) onEscapeRef.current();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      openModals.splice(openModals.indexOf(token), 1);
    };
  }, []);
};

export default useTopmostEscape;
